import { CartActions, CartInfo, CartTicketInfo } from './types';
import type { SerializableCartSnapshot } from './types';
import {
  GASeatsPartial,
  SingleSectorPricing,
  TicketTypeOption,
  isMultiLevelPricing,
} from 'types/seats';
import { Pricing } from 'api/responseTypes';

export const CART_STORAGE_KEY = 'tz_cart';

function isRehydratableSnapshot(value: unknown): value is SerializableCartSnapshot {
  if (!value || typeof value !== 'object') return false;
  const o = value as Record<string, unknown>;
  return (
    typeof o.eventKey === 'string' &&
    Array.isArray(o.tickets) &&
    o.tickets.length > 0 &&
    o.tickets.every(
      (t: unknown) =>
        t != null &&
        typeof t === 'object' &&
        typeof (t as Record<string, unknown>).sector === 'string',
    )
  );
}

export function loadCartFromStorage(): CartInfo {
  const empty: CartInfo = { tickets: [] };
  try {
    const raw = sessionStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return empty;
    const parsed = JSON.parse(raw) as unknown;
    return isRehydratableSnapshot(parsed) ? getCartFromSnapshot(parsed) : empty;
  } catch {
    return empty;
  }
}

export function stateToSnapshot(state: CartInfo): SerializableCartSnapshot | null {
  if (!state.eventKey || state.tickets.length === 0) return null;
  return {
    eventKey: state.eventKey,
    eventId: state.eventId,
    eventInfoId: state.eventInfoId,
    holdToken: state.holdToken,
    promoCode: state.promoCode,
    discountAmount: state.discountAmount,
    invitationId: state.invitationId,
    selectedTimeslot: state.selectedTimeslot,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    tickets: state.tickets.map(({ deselect, ...t }) => t),
  };
}

export enum CART_ACTION {
  SET_MATCH_ID = 'SET_MATCH_ID',
  SET_EVENT_ID = 'SET_EVENT_ID',
  SET_EVENT_KEY = 'SET_EVENT_KEY',
  SET_HOLD_TOKEN = 'SET_HOLD_TOKEN',
  UPDATE_TICKETS = 'UPDATE_TICKETS',
  CLEAR_TICKETS = 'CLEAR_TICKETS',
  CLEAR_DATA = 'CLEAR_DATA',
  SET_PROMO_CODE = 'SET_PROMO_CODE',
  CLEAR_PROMO_CODE = 'CLEAR_PROMO_CODE',
  SET_INVITATION_ID = 'SET_INVITATION_ID',
  SET_TIMESLOT = 'SET_TIMESLOT',
}

function updateMultiLevelTickets(
  state: CartInfo,
  ga: GASeatsPartial,
  ticketTypes: TicketTypeOption[],
  apiPricing: Pricing[],
): CartInfo {
  const selectionPerTicketType = ga.selectionPerTicketType ?? {};
  const categoryPricing = apiPricing.find((p) => p.category === ga.category.key.toString());
  const baseTicket = {
    sector: ga.label,
    category: ga.category.label,
    categoryKey: ga.category.key.toString(),
    categoryColor: ga.category.color,
    entrance: ga.entrance,
  };

  const newTickets: CartTicketInfo[] = ticketTypes.flatMap((option) => {
    const count = selectionPerTicketType[option.ticketType] ?? 0;
    const apiTicketType = categoryPricing?.ticketTypes?.find(
      (t) => t.ticketType === option.ticketType,
    );
    return Array.from({ length: count }, () => ({
      ...baseTicket,
      price: option.price,
      formattedPrice: option.formattedPrice,
      ticketType: option.ticketType,
      ticketLabel: apiTicketType?.label,
      mandatory: apiTicketType?.mandatory,
      deselect: () => ga.deselect(option.ticketType),
    }));
  });

  // Keep tickets from other GA areas / normal seats unchanged, replace this area's tickets
  const otherTickets = state.tickets.filter((ticket) => ticket.sector !== ga.label);
  return { ...state, tickets: [...otherTickets, ...newTickets] };
}

function updateSingleLevelTickets(
  state: CartInfo,
  ga: GASeatsPartial,
  pricing: SingleSectorPricing,
): CartInfo {
  const categoryKey = ga.category.key.toString();
  const otherTickets = state.tickets.filter((ticket) => ticket.categoryKey !== categoryKey);
  const thisCategorySelectedLabels = ga.chart.selectedObjects.filter((label) => {
    const existing = state.tickets.find((ticket) => ticket.sector === label);
    return existing ? existing.categoryKey === categoryKey : label === ga.label;
  });
  const updatedTickets = thisCategorySelectedLabels.map((label) => {
    const prev = state.tickets.find(
      (ticket) => ticket.sector === label && ticket.categoryKey === categoryKey,
    );
    return (
      prev ?? {
        sector: ga.label,
        category: ga.category.label,
        categoryKey,
        categoryColor: ga.category.color,
        price: pricing.price,
        formattedPrice: pricing.formattedPrice,
        deselect: ga.deselect,
        entrance: ga.entrance,
      }
    );
  });
  return { ...state, tickets: [...otherTickets, ...updatedTickets] };
}

export const cartReducer = (currentCartState: CartInfo, action: CartActions): CartInfo => {
  switch (action.type) {
    case CART_ACTION.SET_MATCH_ID:
    case CART_ACTION.SET_EVENT_ID:
    case CART_ACTION.SET_EVENT_KEY:
    case CART_ACTION.SET_HOLD_TOKEN: {
      return { ...currentCartState, ...action.payload };
    }

    case CART_ACTION.UPDATE_TICKETS: {
      const { ga, apiPricing } = action.payload;
      const pricing = ga.pricing;

      if (isMultiLevelPricing(pricing)) {
        return updateMultiLevelTickets(currentCartState, ga, pricing.ticketTypes, apiPricing);
      }

      return updateSingleLevelTickets(currentCartState, ga, pricing);
    }

    case CART_ACTION.CLEAR_TICKETS: {
      return {
        ...currentCartState,
        tickets: [],
      };
    }
    case CART_ACTION.CLEAR_DATA: {
      return {
        tickets: [],
      };
    }
    case CART_ACTION.SET_PROMO_CODE: {
      return {
        ...currentCartState,
        promoCode: action.payload.promoCode,
        discountAmount: action.payload.discountAmount,
      };
    }
    case CART_ACTION.CLEAR_PROMO_CODE: {
      const newState = { ...currentCartState };
      delete newState.promoCode;
      delete newState.discountAmount;
      return newState;
    }
    case CART_ACTION.SET_INVITATION_ID: {
      return { ...currentCartState, ...action.payload };
    }
    case CART_ACTION.SET_TIMESLOT: {
      const { selectedTimeslot } = action.payload;
      if (!selectedTimeslot) {
        const newState = { ...currentCartState };
        delete newState.selectedTimeslot;
        return newState;
      }
      return { ...currentCartState, selectedTimeslot };
    }
  }
};

const noop = (): void => {
  /* placeholder for rehydrated tickets */
};

export function getCartFromSnapshot(snapshot: SerializableCartSnapshot): CartInfo {
  const tickets: CartTicketInfo[] = (snapshot.tickets ?? []).map((t) => ({
    ...t,
    deselect: noop,
  }));
  return {
    tickets,
    ...(snapshot.eventInfoId != null && { eventInfoId: snapshot.eventInfoId }),
    ...(snapshot.eventId != null && { eventId: snapshot.eventId }),
    ...(snapshot.eventKey != null && { eventKey: snapshot.eventKey }),
    ...(snapshot.holdToken != null && { holdToken: snapshot.holdToken }),
    ...(snapshot.promoCode != null && { promoCode: snapshot.promoCode }),
    ...(snapshot.discountAmount != null && { discountAmount: snapshot.discountAmount }),
    ...(snapshot.invitationId != null && { invitationId: snapshot.invitationId }),
    ...(snapshot.selectedTimeslot != null && { selectedTimeslot: snapshot.selectedTimeslot }),
  };
}
